import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "./../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ModifierAndProductMultiSelectionModal from "../../components/CustomComponents/modal/ModifierAndProductMultiSelectionModal.jsx";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import useStableFetchEffect from "../../hooks/useStableFetchEffect";

function UpdateModifierGroup() {
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompID = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const { mGID } = useParams();
  const navigate = useNavigate();
  const isBranchMode = useIsChildBranch();
  const [isParentCompanyModifierGroup, setIsParentCompanyModifierGroup] = useState(false);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();



  const [loading, setLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: "",
    subTitle: "",
    type: "List",
    canSkip: true,
    enableQuantity: true,
    minimumSelection: 0,
    maximumSelection: 0,
    freeSelection: 0,
    extraSelectionCharges: 0,
  });
  const [modifiers, setModifiers] = useState([]);
  const [validationErrors, setValidationErrors] = useState({
    minimumSelection: "",
    maximumSelection: "",
  });
  const [modals, setModals] = useState({
    showSelectItemsModal: false,
  });

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const safeParseFloat = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "") return defaultVal;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const safeParseInt = (value, defaultVal = 0) => {
    if (value === null || value === undefined || value === "") return defaultVal;
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  const validateSelectionFields = useCallback((minVal, maxVal) => {
    const errors = { minimumSelection: "", maximumSelection: "" };
    const minSel = parseInt(minVal) || 0;
    const maxSel = parseInt(maxVal) || 0;
    const modifierCount = Array.isArray(modifiers) ? modifiers.length : 0;

    if (maxSel > 0 && minSel > maxSel) {
      errors.minimumSelection = "Minimum selection cannot be greater than maximum selection";
    }
    if (maxSel > modifierCount && modifierCount > 0) {
      errors.maximumSelection = `Maximum selection cannot be greater than the number of items (${modifierCount})`;
    }

    setValidationErrors(errors);
    return errors.minimumSelection === "" && errors.maximumSelection === "";
  }, [modifiers]);

  const handleModifierChange = (index, field, value) => {
    setModifiers((prev) => {
      const arr = Array.isArray(prev) ? [...prev] : [];
      if (!arr[index]) return arr;

      if (field === "menuItem_IsDefaultModifier") {
        const willBeDefault = Boolean(value);
        return arr.map((m, i) => ({
          ...m,
          menuItem_IsDefaultModifier: i === index ? willBeDefault : false,
        }));
      }

      arr[index] = { ...arr[index], [field]: value };
      return arr;
    });
  };

  const handleRemoveModifier = (index) => {
    setModifiers((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      const next = arr.filter((_, i) => i !== index);
      return next.map((m, i) => ({ ...m, SortingOrder: i + 1 }));
    });
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => e.preventDefault();

  const handleDrop = (e) => {
    e.preventDefault();
    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (draggedIndex === overIndex || draggedIndex === undefined || overIndex === undefined) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const newModifiers = [...modifiers];
    const dragged = newModifiers[draggedIndex];
    newModifiers.splice(draggedIndex, 1);
    newModifiers.splice(overIndex, 0, dragged);
    setModifiers(newModifiers.map((m, i) => ({ ...m, SortingOrder: i + 1 })));

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  useStableFetchEffect(async (isStale) => {
    if (!CompID || !mGID) {
      showErrorToast("Missing required information");
      navigate("/Add-ons/Add-onsGroups");
      return;
    }

    try {
      setInitLoading(true);
      const response = await axiosInstance.get(APIs.GET_MODIFIER_GROUP_BY_ID, {
        params: { CompID, ModifierGroupID: mGID },
      });

      if (isStale()) return;

      const data = response.data?.data || response.data;
      if (!data) {
        showErrorToast("No add-on group data received");
        navigate("/Add-ons/Add-onsGroups");
        return;
      }

      if (isBranchMode && data) {
        const modifierGroupCompID = data.CompID || data.compID;
        if (modifierGroupCompID && parentCompID && modifierGroupCompID === parentCompID) {
          setIsParentCompanyModifierGroup(true);
        }
      }

      setFormData({
        name: data.name || "",
        subTitle: data.subTitle || "",
        type: data.type || "List",
        canSkip: Boolean(data.canSkip ?? true),
        enableQuantity: Boolean(data.enableQuantity ?? true),
        minimumSelection: safeParseInt(data.minimumSelection),
        maximumSelection: safeParseInt(data.maximumSelection),
        freeSelection: safeParseInt(data.freeSelection),
        extraSelectionCharges: safeParseFloat(data.extraSelectionCharges),
      });

      const raw = Array.isArray(data.modifiers) ? data.modifiers : [];
      const normalized = raw
        .map((m, idx) => {
          const id = Number(m?.menuItem_ID ?? m?.MenuItem_ID ?? m?.menuItemId ?? m?.id) || 0;
          if (id <= 0) return null;
          const isDefault = Boolean(m?.IsDefault ?? m?.isDefault ?? m?.menuItem_IsDefaultModifier);
          const sorting = Number(m?.SortingOrder ?? m?.sortingOrder ?? idx + 1) || idx + 1;
          return {
            menuItem_ID: id,
            menuItem_Name: m?.menuItem_Name ?? m?.MenuItem_Name ?? "",
            menuItem_Type: m?.menuItem_Type ?? m?.MenuItem_Type ?? "",
            menuItem_IsDefaultModifier: isDefault,
            SortingOrder: sorting,
            raw: m,
          };
        })
        .filter(Boolean)
        .sort((a, b) => (a.SortingOrder || 0) - (b.SortingOrder || 0))
        .map((m, i) => ({ ...m, SortingOrder: i + 1 }));

      setModifiers(normalized);
    } catch (e) {
      if (isStale()) return;
      showErrorToast("Failed to load add-on group");
      navigate("/Add-ons/Add-onsGroups");
    } finally {
      if (!isStale()) setInitLoading(false);
    }
  }, [CompID, mGID, parentCompID, isBranchMode]);

  const handleSubmit = async () => {
    // Prevent update if this is a branch viewing a parent company modifier group
    if (isBranchMode && isParentCompanyModifierGroup) {
      return;
    }
    
    try {
      setLoading(true);

      if (!formData.name?.trim()) {
        showErrorToast("Name is required");
        return;
      }

      const selectionValid = validateSelectionFields(formData.minimumSelection, formData.maximumSelection);
      if (!selectionValid) {
        showErrorToast("Please fix selection limits");
        return;
      }

      const validModifiers = Array.isArray(modifiers)
        ? modifiers
            .filter((m) => Number(m?.menuItem_ID) > 0 && m?.menuItem_Name)
            .sort((a, b) => (parseInt(a.SortingOrder) || 0) - (parseInt(b.SortingOrder) || 0))
            .map((m, idx) => ({
              menuItem_ID: Number(m.menuItem_ID) || 0,
              menuItem_Name: m.menuItem_Name || "",
              compID: CompID,
              menuItem_IsDefaultModifier: Boolean(m.menuItem_IsDefaultModifier),
              IsDefault: Boolean(m.menuItem_IsDefaultModifier),
              SortingOrder: idx + 1,
            }))
        : [];

      if (validModifiers.length === 0) {
        showErrorToast("At least one item must be selected");
        return;
      }

      const payload = {
        CompID,
        modifierGroupID: mGID,
        ...formData,
        minimumSelection: safeParseInt(formData.minimumSelection),
        maximumSelection: safeParseInt(formData.maximumSelection),
        freeSelection: safeParseInt(formData.freeSelection),
        extraSelectionCharges: safeParseFloat(formData.extraSelectionCharges),
        detail: "",
        updateDatetime: new Date().toISOString(),
        Modifiers: validModifiers,
      };

      const response = await axiosInstance.put(
        APIs.PUT_UPDATE_MODIFIER_GROUP,
        payload
      );

      if (response.data.status) {
        showSuccessToast("Modifier Group updated successfully");
        navigate("/Add-ons/Add-onsGroups");
      } else {
        throw new Error(
          response.data?.message || "Failed to update Add-on Group"
        );
      }
    } catch (error) {
      showErrorToast(
        error.response?.data?.message ||
        error.message ||
        "An error occurred while updating the Add-on Group"
      );
    } finally {
      setLoading(false);
    }
  };
  // Loading screen for initial load
  if (initLoading) {
    return <Preloader />;
  }

  // Error boundary fallback
  if (!CompID || !mGID) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "50vh" }}
      >
        <div className="text-center">
          <h3>Error Loading Add-on Group</h3>
          <p>Missing required parameters. Please try again.</p>
          <Link
            to="/modifiers/manageModifierGroups"
            className="btn btn-primary"
          >
            Back to Add-on Groups
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item">
              <Link to="/Add-ons/Add-onsGroups">
                Manage Add-on Groups
              </Link>
            </li>
            <li className="breadcrumb-item active">Update Add-on Group</li>
          </ol>
          <h1 className="page-header mb-0">Update Add-on Group</h1>
        </div>
        {!(isBranchMode && isParentCompanyModifierGroup) && (
          <div className="ms-auto">
            <button
              onClick={handleSubmit}
              className="btn px-4"
              disabled={loading || !formData.name?.trim()}
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: 'none',
                color: 'white',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                }
              }}
            >
              <i className="fa-lg me-2 ms-n2 text-white"></i>
              {loading ? "Updating..." : "Submit"}
            </button>
          </div>
        )}
      </div>

      <div className="card border-0">
        <Panel className="m-3">
          <PanelHeader>Modifier Group Details</PanelHeader>
          <PanelBody>
            <div className="p-2">
              <div className="row mb-4">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Name *"
                      placeHolder="Enter Add-on Group Name"
                      type="text"
                      value={formData.name}
                      onchange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Subtitle"
                      placeHolder="Enter Subtitle"
                      type="text"
                      value={formData.subTitle}
                      onchange={(e) => setFormData((p) => ({ ...p, subTitle: e.target.value }))}
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Minimum Selection"
                      placeHolder="Enter Minimum Selection"
                      type="number"
                      min="0"
                      value={formData.minimumSelection}
                      onchange={(e) => {
                        setFormData((p) => ({ ...p, minimumSelection: e.target.value }));
                        validateSelectionFields(e.target.value, formData.maximumSelection);
                      }}
                      error={validationErrors.minimumSelection}
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                    />
                    {validationErrors.minimumSelection && (
                      <small className="text-danger d-block mt-1">
                        {validationErrors.minimumSelection}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Maximum Selection"
                      placeHolder="Enter Maximum Selection"
                      type="number"
                      min="0"
                      value={formData.maximumSelection}
                      onchange={(e) => {
                        setFormData((p) => ({ ...p, maximumSelection: e.target.value }));
                        validateSelectionFields(formData.minimumSelection, e.target.value);
                      }}
                      error={validationErrors.maximumSelection}
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                    />
                    {validationErrors.maximumSelection && (
                      <small className="text-danger d-block mt-1">
                        {validationErrors.maximumSelection}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <FormInput
                      label="Free Selection"
                      placeHolder="Enter Free Selection Count"
                      type="number"
                      min="0"
                      value={formData.freeSelection}
                      onchange={(e) =>
                        setFormData((p) => ({ ...p, freeSelection: e.target.value }))
                      }
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={formData.canSkip}
                      onToggle={() =>
                        setFormData((p) => ({ ...p, canSkip: !p.canSkip }))
                      }
                      label="Can Skip"
                      disabled={loading || (isBranchMode && isParentCompanyModifierGroup)}
                      themeColor={themeColor}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <ToggleSwitch
                      isChecked={formData.enableQuantity}
                      onToggle={() =>
                        setFormData((p) => ({ ...p, enableQuantity: !p.enableQuantity }))
                      }
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                      label="Enable Quantity"
                      disabled={loading || (isBranchMode && isParentCompanyModifierGroup)}
                      themeColor={themeColor}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Add-ons & Products</h5>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        if (!(isBranchMode && isParentCompanyModifierGroup)) {
                          setModals((p) => ({ ...p, showSelectItemsModal: true }));
                        }
                      }}
                      disabled={isBranchMode && isParentCompanyModifierGroup}
                      style={{
                        backgroundColor: themeColor,
                        border: "none",
                        borderRadius: "8px",
                        padding: "8px 16px",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      Select Items
                    </button>
                  </div>

                  <div className="table-responsive">
                    <table className="table">
                      <thead style={{ backgroundColor: "#d9d9d9" }}>
                        <tr>
                          <th>Item</th>
                          <th>Type</th>
                          <th>Cost Price</th>
                          <th>Sale Price</th>
                          <th>Default</th>
                          <th>Sort Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modifiers.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="text-center py-4">
                              No items selected. Click <strong>Select Items</strong> to add add-ons/products.
                            </td>
                          </tr>
                        ) : (
                          modifiers.map((modifier, index) => (
                            <tr
                              key={`${modifier.menuItem_ID}-${index}`}
                              draggable={!loading}
                              onDragStart={(e) => handleDragStart(e, index)}
                              onDragEnter={(e) => handleDragEnter(e, index)}
                              onDragLeave={handleDragLeave}
                              onDragOver={handleDragOver}
                              onDrop={handleDrop}
                              onDragEnd={handleDragEnd}
                            >
                              <td className="align-middle" style={{ fontWeight: 600 }}>
                                {modifier.menuItem_Name}
                              </td>
                              <td className="align-middle">
                                <span className="badge bg-secondary">
                                  {(modifier.raw?.menuItem_Type || modifier.raw?.MenuItem_Type || modifier.menuItem_Type || "Item") === "Modifier"
                                    ? "Add-on"
                                    : "Product"}
                                </span>
                              </td>
                              <td className="align-middle">
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: `${themeColor}10`,
                                    color: isDarkMode ? "#e2e8f0" : "#495057",
                                    fontWeight: 600,
                                    padding: "8px 10px",
                                  }}
                                >
                                  {(() => {
                                    const raw = modifier.raw || modifier;
                                    const v =
                                      raw?.menuItem_CostPrice ??
                                      raw?.MenuItem_CostPrice ??
                                      raw?.menuItem_Cost ??
                                      raw?.MenuItem_Cost ??
                                      0;
                                    const n = parseFloat(v);
                                    return isNaN(n) ? "0.00" : n.toFixed(2);
                                  })()}
                                </span>
                              </td>
                              <td className="align-middle">
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: `${themeColor}10`,
                                    color: isDarkMode ? "#e2e8f0" : "#495057",
                                    fontWeight: 600,
                                    padding: "8px 10px",
                                  }}
                                >
                                  {(() => {
                                    const raw = modifier.raw || modifier;
                                    const v =
                                      raw?.menuItem_SalePrice ??
                                      raw?.MenuItem_SalePrice ??
                                      raw?.menuItem_Price ??
                                      raw?.MenuItem_Price ??
                                      0;
                                    const n = parseFloat(v);
                                    return isNaN(n) ? "0.00" : n.toFixed(2);
                                  })()}
                                </span>
                              </td>
                              <td className="align-middle">
                                <ToggleSwitch
                                  isChecked={Boolean(modifier.menuItem_IsDefaultModifier)}
                                  label="Default"
                                  onToggle={() =>
                                    handleModifierChange(
                                      index,
                                      "menuItem_IsDefaultModifier",
                                      !modifier.menuItem_IsDefaultModifier
                                    )
                                  }
                                  themeColor={themeColor}
                                  disabled={isBranchMode && isParentCompanyModifierGroup}
                                />
                              </td>
                              <td className="align-middle">
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: `${themeColor}15`,
                                    color: themeColor,
                                    fontWeight: 700,
                                    padding: "8px 10px",
                                  }}
                                >
                                  {index + 1}
                                </span>
                              </td>
                              <td className="align-middle">
                                <div className="d-flex align-items-center m-1">
                                  <button
                                    type="button"
                                    className="btn btn-link text-danger p-1"
                                    onClick={() => {
                                      if (!(isBranchMode && isParentCompanyModifierGroup)) {
                                        handleRemoveModifier(index);
                                      }
                                    }}
                                    style={{
                                      cursor: (isBranchMode && isParentCompanyModifierGroup) ? 'not-allowed' : 'pointer',
                                      opacity: (isBranchMode && isParentCompanyModifierGroup) ? 0.5 : 1,
                                      pointerEvents: (isBranchMode && isParentCompanyModifierGroup) ? 'none' : 'auto'
                                    }}
                                    disabled={loading || (isBranchMode && isParentCompanyModifierGroup)}
                                    title="Remove item"
                                  >
                                    <i className="fa fa-trash"></i>
                                  </button>
                                  <i
                                    className="fa fa-bars ms-2"
                                    style={{
                                      cursor: loading ? "not-allowed" : "grab",
                                    }}
                                    title="Drag to reorder"
                                  ></i>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      <ModifierAndProductMultiSelectionModal
        showModal={modals.showSelectItemsModal}
        setShowModal={(val) => setModals((prev) => ({ ...prev, showSelectItemsModal: val }))}
        compID={CompID}
        selectedItems={modifiers}
        onSubmit={(selected) => {
          const arr = Array.isArray(selected) ? selected : [];
          const normalized = arr
            .filter((x) => Number(x?.menuItem_ID) > 0 && x?.menuItem_Name)
            .map((x, idx) => {
              const existing = modifiers.find((m) => Number(m.menuItem_ID) === Number(x.menuItem_ID));
              return {
                ...x,
                menuItem_IsDefaultModifier: Boolean(existing?.menuItem_IsDefaultModifier),
                SortingOrder: idx + 1,
              };
            });

          // Keep single default if already exists in previous selection
          const hasDefault = normalized.some((m) => m.menuItem_IsDefaultModifier);
          if (!hasDefault) {
            // no-op
          } else {
            let firstSeen = false;
            for (const m of normalized) {
              if (m.menuItem_IsDefaultModifier && !firstSeen) firstSeen = true;
              else if (m.menuItem_IsDefaultModifier && firstSeen) m.menuItem_IsDefaultModifier = false;
            }
          }

          setModifiers(normalized);
        }}
      />
    </div>
  );
}

export default UpdateModifierGroup;